import { useState, useRef, useEffect } from "react";
import { Plus, X, ArrowLeft, RotateCcw, Sparkles, ImageIcon, Loader2, FolderOpen, Search, Save, Trash2, User, Globe, Wand2, Layers, ZoomIn, ZoomOut, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { pipelinesApi, mistApi, promptsApi, streamAsset, pipelineErr, guardToast } from "@/api/mist";
import { previewUrl, compressToTarget } from "@/lib/imageCompress";
import { consumeDirectorPrompt } from "@/lib/directorPrefill";
import { ExportHandlers } from "@/components/mist/shared";
import CollageComposer from "@/components/mist/CollageComposer";
import VanCabinetDesigner from "@/components/mist/VanCabinetDesigner";
import { useSession } from "@/context/SessionContext";

const MAX_CHARS = 2500;
const setRenderBusy = (active) =>
  window.dispatchEvent(new CustomEvent("mist:director-render", { detail: { active } }));

const STYLES = [
  { k: "hyper_realistic", label: "Hyper-Realistic Studio Photography", desc: "hyper-realistic studio photography, flawless skin and material detail, professional softbox lighting, shot on a full-frame DSLR with an 85mm lens, razor-sharp focus, editorial magazine quality" },
  { k: "street_photo", label: "Gritty Realistic Street Photography", desc: "gritty realistic street photography, candid documentary framing, natural available light, subtle film grain, 35mm reportage aesthetic, authentic raw urban texture" },
  { k: "pixar_3d", label: "3D Pixar / Disney Game Graphic", desc: "polished 3D Pixar / Disney-style character render, soft subsurface skin, expressive stylized features, cinematic studio lighting, glossy high-fidelity game cinematic graphic" },
  { k: "cyber_gothic", label: "Cyber-Gothic / Y2K Futuristic", desc: "cyber-gothic Y2K futuristic aesthetic, dark chrome and glossy black surfaces, iridescent metallic accents, early-2000s techno-gothic fashion, moody neon-lit futurism" },
  { k: "synthwave", label: "Synthwave / Neon Grid", desc: "synthwave neon-grid aesthetic, retro 80s sunset, glowing magenta and cyan neon, chrome and laser grid horizon, outrun retrofuturism" },
  { k: "dreamcore", label: "Dreamcore / Neo-Surrealism", desc: "dreamcore neo-surrealism, hazy nostalgic liminal atmosphere, soft uncanny lighting, surreal dreamlike composition, faded film wash" },
  { k: "acid_grunge", label: "Acid Graphics / Neo-Grunge Rave", desc: "acid graphics neo-grunge rave aesthetic, distressed torn textures, chaotic acid-bright colors, glitch and photocopy grit, Y2K rave flyer energy" },
  { k: "psychedelic", label: "Psychedelic / Fluid Abstract", desc: "psychedelic fluid abstract art, swirling liquid color, kaleidoscopic patterns, trippy iridescent gradients, hyper-saturated flowing forms" },
];

// Game-screenshot positioning template. Layout/mechanics locked; the [input]
// slots are yours to fill (the skin/theme). Loaded into the prompt via the toggle.
const GAME_TEMPLATE = `A vertical mobile game screenshot straight down a cracked {ROAD_STYLE} road.

At the bottom foreground, a tactical squad of the hero characters from the provided reference image(s) (match their exact design) arranged in a pyramid formation: 3 characters lined up across the centre at roughly 15% up from the bottom edge, and 2 characters lined up across the centre at roughly 25% up from the bottom edge. They are rendered in the game's colorful 3D low-poly style, huddled together, seen from behind, looking forward up the road. Keep the squad within a 35% bottom-edge zone. Reduce the character size by 40%.

The road is a 2-lane road, with a 10% buffer in from the left edge and a 10% buffer in from the right edge at the bottom.

On the road lanes ahead of them: in the LEFT lane, a single glowing neon-blue window gate with bold white text reading "+100", at roughly 45% up from the bottom edge. Staggered in the RIGHT lane, a glowing neon-red window gate with bold white text reading "-50", at roughly 40% down from the top edge.

Further up the road behind each lane's window gate, blocking the path, are the enemy and zombie characters from the provided enemy reference image(s) (match their exact design). In the deep background the alien and zombie enemies are scattered widely, overflowing off the road edges and spreading freely into the desert margins on the left and right sides. But as the horde moves forward down the track closer to the bottom edge, their path narrows drastically, forcing the alien and zombie characters to squeeze into a tight, narrow bottleneck centred strictly on the asphalt road as they approach the heroes. Some stand next to 1 {OBSTACLE_TYPE} lying on its side across the left lane at roughly 55% up from the bottom edge, with small white text reading "15". The right lane has enemies standing next to 1 {OBSTACLE_TYPE} standing upright with a floating weapon pickup hovering on top, at roughly 65% up from the bottom edge, with small white text reading "15". Up here the road narrows to a 12% buffer in from the left edge and a 12% buffer in from the right edge toward the top.

At the absolute top-centre end of the straight road, a massive giant alpha goliath boss alien creature from the provided boss reference image(s) looms majestically in front of the far horizon, blocking the final destination point.

The background is {BACKGROUND_SCENERY}.

In the top left corner, plain white game text reads "stage {STAGE_NUMBER}".

Stylized mobile hyper-casual game art design, colorful 3D low-poly models, vibrant lighting, smooth glossy textures, clean game user interface, casual army strategy game aesthetic, Unity 3D engine style.

Negative prompts: Do NOT render any numbered "%" values or buffer numbers as text on the image. Maintain strict separation between the heroes at the bottom and the enemies at the top — do NOT mix them up. Do NOT restrict the regular alien/zombie enemy variants strictly to the road lanes in the far background (there they should overflow into the desert margins). Do NOT hallucinate or invent extra elements to fill a space. Maintain strict adherence to this prompt.`;

// 5 quick-pick skins — each fills the 3 theme tokens (ROAD_STYLE, OBSTACLE_TYPE,
// BACKGROUND_SCENERY). Everything else (camera, gates, heroes, circles, HUD) is
// locked into GAME_TEMPLATE, so filling these produces a complete Last-War prompt.
const SKIN_PRESETS = [
  { key: "desert", emoji: "🏜️", label: "Desert Wasteland", fills: {
    ROAD_STYLE: "dusty asphalt",
    OBSTACLE_TYPE: "wooden wine barrel",
    BACKGROUND_SCENERY: "a desert wasteland with ruined concrete houses, a run-down bar toward the top-left edge, and a massive futuristic sci-fi bunker in the far distance under a crimson sky with planets visible" } },
  { key: "alien", emoji: "🪐", label: "Alien Planet", fills: {
    ROAD_STYLE: "neon purple crystalline",
    OBSTACLE_TYPE: "glowing organic alien egg pod",
    BACKGROUND_SCENERY: "a surreal purple alien planet landscape with organic hive spires, strange flora, and massive ringed gas giant planets dominating the sky" } },
  { key: "race", emoji: "🏎️", label: "Race Track", fills: {
    ROAD_STYLE: "polished black racetrack asphalt with yellow lane markings",
    OBSTACLE_TYPE: "stack of red and white racing tires",
    BACKGROUND_SCENERY: "a high-tech modern stadium circuit racetrack with bright stadium lights, grandstands full of spectators, and a clear blue sunny sky" } },
  { key: "frozen", emoji: "❄️", label: "Frozen Tundra", fills: {
    ROAD_STYLE: "snow-covered ice trail",
    OBSTACLE_TYPE: "frozen wooden supply crate covered in icicles",
    BACKGROUND_SCENERY: "a vast frozen arctic tundra landscape with majestic snowy mountains, pine trees heavy with snow, and a vibrant green aurora borealis dancing across the sky" } },
  { key: "cyber", emoji: "🏙️", label: "Cyberpunk City", fills: {
    ROAD_STYLE: "rain-slicked dark cyber asphalt reflecting neon lights",
    OBSTACLE_TYPE: "futuristic heavy metal server block with blinking lights",
    BACKGROUND_SCENERY: "a dense futuristic cyberpunk metropolis at night with towering dark skyscrapers covered in giant holographic advertisements, flying glowing cars, and a foggy cybernetic skyline" } },
];
const applySkin = (p, roles = {}, stage = "1") => {
  const fills = { ...p.fills, STAGE_NUMBER: String(stage || "1") };
  let out = GAME_TEMPLATE;
  Object.entries(fills).forEach(([k, v]) => { out = out.split(`{${k}}`).join(v); });
  if (roles.hero) out = out.replace("the hero characters from the provided reference image(s) (match their exact design)", "the hero characters from the provided hero reference images (match their exact design)");
  if (roles.enemy) out = out.replace("the enemy and zombie characters from the provided enemy reference image(s) (match their exact design)", "the enemy and zombie characters from the provided enemy reference images (match their exact design)");
  if (roles.soldier) out = out.replace("Keep the squad within a 35% bottom-edge zone.", "Keep the squad within a 35% bottom-edge zone. Closest to the very bottom edge, directly behind and wrapping around the rear and sides of the hero pyramid, a crowd of miniature clone soldiers from the provided mini-soldier reference image forms a tight U-shaped wall, staying narrow and compact within about a single lane's width.");
  return out;
};
const REF_CAP = { hero: 5, soldier: 1, enemy: 4, boss: 1, ref: 5 };
const rolesOf = (list) => ({ hero: list.some((r) => r.role === "hero"), soldier: list.some((r) => r.role === "soldier"), enemy: list.some((r) => r.role === "enemy"), boss: list.some((r) => r.role === "boss") });

// Built-in starter prompts — always available in the Prompt Folder (Personal tab).
// The blank game template + one entry per skin, so the user's core templates are
// one tap away without re-pasting.
const STARTER_PROMPTS = [
  { id: "starter-base", starter: true, name: "Game Screenshot — Blank Template", ratio: "9:16", stage: "1", skin: null, style: null, template: true, prompt_text: GAME_TEMPLATE.split("{STAGE_NUMBER}").join("1") },
  { id: "starter-character-scene", starter: true, name: "Generic Character Scene", ratio: "9:16", stage: "1", skin: null, style: null, template: true, prompt_text: "A full length shot photo of a [AGE/STYLE] [woman/man] with a [HEIGHT/BUILD] frame. [She/he] is of [ETHNICITY] descent, featuring striking [EYE COLOUR] eyes and [HAIR STYLE/LENGTH/COLOUR]. [She/he] is wearing [WEARING / OUTFIT DETAILS]. The scene is set in a [BACKGROUND/ENVIRONMENT] with [LIGHTING STYLE] lighting, creating a realistic, high-detail editorial photography aesthetic." },
  ...SKIN_PRESETS.map((p) => ({ id: `starter-${p.key}`, starter: true, name: `${p.emoji} ${p.label} Skin`, ratio: "9:16", stage: "1", skin: p.key, style: null, template: true, prompt_text: applySkin(p, {}, "1") })),
];

function PromptRow({ p, onLoad, onDelete }) {
  const slotCount = (p.segments || []).filter((s) => s.t === "slot").length;
  return (
    <div className="rounded-xl border border-slate-700 bg-slate-900 p-3" data-testid={`prompt-row-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono text-[11px] font-black text-slate-100 truncate">{p.name}</p>
          <p className="font-mono text-[9px] text-slate-500 mt-0.5 line-clamp-2">{p.prompt_text}</p>
          <div className="flex items-center gap-2 mt-1">
            {!p.starter && <span className="font-mono text-[8px] text-cyan-500/70">by {p.by}</span>}
            {slotCount > 0 && <span className="inline-flex items-center gap-1 font-mono text-[8px] font-black uppercase tracking-wide text-fuchsia-300 bg-fuchsia-500/15 border border-fuchsia-400/40 rounded px-1.5 py-0.5"><Wand2 size={9} /> {slotCount} editable</span>}
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-1.5">
          {onDelete && (
            <button onClick={onDelete} data-testid={`prompt-delete-${p.id}`}
              className="w-8 h-8 grid place-items-center rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-300 active:scale-95"><Trash2 size={13} /></button>
          )}
          <button onClick={onLoad} data-testid={`prompt-load-${p.id}`}
            className="rounded-lg bg-cyan-400 text-slate-950 font-mono text-[10px] font-black uppercase tracking-widest px-3 py-2 active:scale-95">Load</button>
        </div>
      </div>
    </div>
  );
}

// Flexible-prompt editor — inline highlight preview + a quick-edit field per slot.
// Editing a slot recompiles the full prompt (join of all segments) upstream.
function FlexSlotEditor({ segments, onSlot }) {
  const slots = segments.map((s, i) => ({ ...s, i })).filter((s) => s.t === "slot");
  if (!slots.length) return null;
  return (
    <div className="rounded-xl border border-fuchsia-400/40 bg-fuchsia-500/[0.07] p-3 space-y-3" data-testid="flex-slot-editor">
      <div className="flex items-center gap-2">
        <Wand2 size={13} className="text-fuchsia-300" />
        <span className="font-mono text-[10px] font-black uppercase tracking-widest text-fuchsia-300">Flexible Prompt · edit the highlighted parts</span>
      </div>
      <p className="font-mono text-[11px] leading-relaxed text-slate-300" data-testid="flex-slot-preview">
        {segments.map((s, i) => s.t === "slot"
          ? <span key={i} className="rounded bg-fuchsia-500/25 text-fuchsia-100 px-1 border border-fuchsia-400/40">{s.v || "…"}</span>
          : <span key={i}>{s.v}</span>)}
      </p>
      <div className="space-y-2">
        {slots.map((s) => (
          <label key={s.i} className="block">
            <span className="font-mono text-[9px] font-black uppercase tracking-widest text-fuchsia-300">{s.label}</span>
            <textarea value={s.v} onChange={(e) => onSlot(s.i, e.target.value)} rows={2} data-testid={`flex-slot-${(s.label || "edit").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-[12px] text-slate-100 outline-none focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-400/30 resize-none" />
          </label>
        ))}
      </div>
    </div>
  );
}

// Drag-&-place Layout Composer — drop references onto a background canvas, then
// flatten the arrangement into one guide image that steers Nano-Banana placement
// (far less prompt typing). Returns a layout_guide.png File via onDone.
function LayoutComposer({ refs, onCancel, onDone }) {
  const [bgId, setBgId] = useState(refs[0]?.id || null);
  const [activeId, setActiveId] = useState(refs.find((r) => r.id !== refs[0]?.id)?.id || null);
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);
  const drag = useRef(null);
  const bg = refs.find((r) => r.id === bgId);

  const place = (e) => {
    if (!activeId || drag.current) return;
    const stamp = refs.find((r) => r.id === activeId);
    if (!stamp) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const key = `${activeId}-${Date.now()}`;
    setItems((p) => [...p, { key, refId: activeId, src: stamp.src, x, y, scale: 1, rot: 0 }]);
    setSel(key);
  };
  const onDown = (e, key) => { e.stopPropagation(); setSel(key); drag.current = { key, rect: wrapRef.current.getBoundingClientRect() }; };
  const onMove = (e) => {
    if (!drag.current) return;
    const { key, rect } = drag.current;
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setItems((p) => p.map((it) => (it.key === key ? { ...it, x, y } : it)));
  };
  const onUp = () => { setTimeout(() => { drag.current = null; }, 0); };
  const adj = (key, dS, dR) => setItems((p) => p.map((it) => it.key === key ? { ...it, scale: Math.max(0.2, Math.min(3, it.scale + dS)), rot: it.rot + dR } : it));
  const del = (key) => { setItems((p) => p.filter((it) => it.key !== key)); setSel(null); };
  const loadImg = (src) => new Promise((res, rej) => { const im = new Image(); im.crossOrigin = "anonymous"; im.onload = () => res(im); im.onerror = rej; im.src = src; });

  const flatten = async () => {
    if (!bg) { toast.error("Pick a background (tap ★ on a reference)"); return; }
    setBusy(true);
    try {
      const W = 720, H = 1280;
      const cv = document.createElement("canvas"); cv.width = W; cv.height = H;
      const ctx = cv.getContext("2d");
      const bgImg = await loadImg(bg.src);
      const s = Math.max(W / bgImg.width, H / bgImg.height);
      ctx.drawImage(bgImg, W / 2 - bgImg.width * s / 2, H / 2 - bgImg.height * s / 2, bgImg.width * s, bgImg.height * s);
      for (const it of items) {
        const im = await loadImg(it.src);
        const drawW = W * 0.26 * it.scale;
        const drawH = drawW * (im.height / im.width);
        ctx.save();
        ctx.translate((it.x / 100) * W, (it.y / 100) * H);
        ctx.rotate((it.rot * Math.PI) / 180);
        ctx.drawImage(im, -drawW / 2, -drawH / 2, drawW, drawH);
        ctx.restore();
      }
      const blob = await new Promise((r) => cv.toBlob(r, "image/png", 0.92));
      await onDone(new File([blob], "layout_guide.png", { type: "image/png" }));
    } catch (e) { toast.error("Couldn't build the layout"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-950 flex flex-col" data-testid="wizard-composer">
      <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <span className="font-mono text-[12px] font-black uppercase tracking-widest text-cyan-300">Arrange Layout</span>
        <button onClick={onCancel} data-testid="composer-close" className="w-8 h-8 grid place-items-center rounded-full bg-slate-800 text-slate-200"><X size={16} /></button>
      </div>
      <div className="flex-1 min-h-0 grid place-items-center p-3">
        <div ref={wrapRef} onClick={place} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp} data-testid="composer-canvas"
          className="relative bg-black border-2 border-cyan-400/40 overflow-hidden" style={{ width: "min(86vw, 44vh)", aspectRatio: "9/16" }}>
          {bg && <img src={bg.src} alt="bg" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />}
          {items.map((it) => (
            <div key={it.key} onPointerDown={(e) => onDown(e, it.key)} data-testid={`composer-item-${it.refId}`}
              className={`absolute touch-none ${sel === it.key ? "ring-2 ring-cyan-400" : ""}`}
              style={{ left: `${it.x}%`, top: `${it.y}%`, width: `${26 * it.scale}%`, transform: `translate(-50%,-50%) rotate(${it.rot}deg)` }}>
              <img src={it.src} alt="stamp" className="w-full h-auto pointer-events-none select-none" draggable={false} />
            </div>
          ))}
          {items.length === 0 && <div className="absolute inset-x-0 bottom-2 text-center font-mono text-[9px] text-white/60 pointer-events-none">Tap a stamp below · then tap here to place it</div>}
        </div>
      </div>
      {/* Big finger-friendly controls for the selected image */}
      {sel && items.some((it) => it.key === sel) && (
        <div className="shrink-0 px-3 pt-2" data-testid="composer-controls">
          <div className="grid grid-cols-4 gap-2">
            <button onClick={() => adj(sel, -0.18, 0)} data-testid="composer-scale-down" className="h-12 rounded-xl bg-slate-800 border-2 border-slate-600 grid place-items-center text-cyan-300 active:scale-95"><ZoomOut size={22} /></button>
            <button onClick={() => adj(sel, 0.18, 0)} data-testid="composer-scale-up" className="h-12 rounded-xl bg-slate-800 border-2 border-slate-600 grid place-items-center text-cyan-300 active:scale-95"><ZoomIn size={22} /></button>
            <button onClick={() => adj(sel, 0, 15)} data-testid="composer-rotate" className="h-12 rounded-xl bg-slate-800 border-2 border-slate-600 grid place-items-center text-cyan-300 active:scale-95"><RotateCw size={22} /></button>
            <button onClick={() => del(sel)} data-testid="composer-item-del" className="h-12 rounded-xl bg-rose-500/15 border-2 border-rose-500/50 grid place-items-center text-rose-300 active:scale-95"><Trash2 size={22} /></button>
          </div>
          <p className="mt-1 text-center font-mono text-[8px] uppercase tracking-widest text-slate-500">Editing selected image · tap another to switch</p>
        </div>
      )}

      <div className="shrink-0 border-t border-slate-800 px-3 py-2 mt-2">
        <p className="font-mono text-[8px] uppercase tracking-widest text-slate-500 mb-1">Tap = active stamp · ★ = set as background</p>
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {refs.map((r) => (
            <div key={r.id} className="relative shrink-0">
              <button onClick={() => setActiveId(r.id)} data-testid={`composer-stamp-${r.id}`}
                className={`w-14 h-14 rounded-lg overflow-hidden border-2 ${activeId === r.id ? "border-cyan-400" : "border-slate-700"} ${bgId === r.id ? "opacity-40" : ""}`}>
                <img src={r.src} alt="ref" className="w-full h-full object-cover" />
              </button>
              <button onClick={() => setBgId(r.id)} data-testid={`composer-bg-${r.id}`}
                className={`absolute -top-1 -right-1 w-5 h-5 grid place-items-center rounded-full border text-[10px] ${bgId === r.id ? "bg-cyan-400 text-slate-950 border-cyan-300" : "bg-slate-900 text-slate-400 border-slate-600"}`}>★</button>
            </div>
          ))}
        </div>
      </div>
      <div className="shrink-0 px-4 py-3 border-t border-slate-800">
        <button onClick={flatten} disabled={busy || !bg} data-testid="composer-done"
          className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-950 font-black font-mono text-sm uppercase tracking-widest py-3.5 disabled:opacity-40">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Layers size={16} />} Use This Layout
        </button>
      </div>
    </div>
  );
}

function ResultImage({ assetId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url, alive = true;
    streamAsset(assetId).then((u) => { if (alive) { url = u; setSrc(u); } }).catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [assetId]);
  if (!src) return <div className="w-full h-full grid place-items-center"><Loader2 size={26} className="text-cyan-400 animate-spin" /></div>;
  return <img src={src} alt="generated" className="w-full h-full object-contain" data-testid="wizard-result-image" />;
}

// Mist Create → Generate. Clean 3-step wizard: config → loading → result.
// Component stays mounted so "Go Back" preserves every input; "Reset" clears.
export default function TextToImageWizard() {
  const [step, setStep] = useState(1);
  const [templateOn, setTemplateOn] = useState(false);
  const [collageOn, setCollageOn] = useState(false);
  const [cadOn, setCadOn] = useState(false);
  const { user } = useSession();
  const isFounder = user?.role === "founder";
  const canCad = isFounder || user?.ad_maker_category === "BUSINESS";
  const [activeSkin, setActiveSkin] = useState(null);
  const [stageNum, setStageNum] = useState("1");
  const [prompt, setPrompt] = useState(() => consumeDirectorPrompt());
  const [fileName, setFileName] = useState("");
  const [style, setStyle] = useState(null);
  const [ratio, setRatio] = useState("9:16");
  const [refs, setRefs] = useState([]); // [{id, src}]
  const [composerOpen, setComposerOpen] = useState(false);
  const [layoutGuide, setLayoutGuide] = useState(null); // {id, src} flattened layout blueprint
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const refInput = useRef(null);
  const pendingRole = useRef("ref");
  const progTimer = useRef(null);

  // Prompt Folder — searchable personal/community library of saved prompts.
  const [folderOpen, setFolderOpen] = useState(false);
  const [folderTab, setFolderTab] = useState("personal");
  const [folderQuery, setFolderQuery] = useState("");
  const [saved, setSaved] = useState([]);
  const [folderBusy, setFolderBusy] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveBusy, setSaveBusy] = useState(false);
  const [flexSegments, setFlexSegments] = useState(null); // editable slots for the current prompt (if any)

  const loadFolder = async (tab, q) => {
    setFolderBusy(true);
    try { const r = await promptsApi.list(tab, q); setSaved(r.prompts || []); }
    catch { toast.error("Couldn't load your prompt folder"); }
    finally { setFolderBusy(false); }
  };
  useEffect(() => {
    if (!folderOpen) return;
    const t = setTimeout(() => loadFolder(folderTab, folderQuery), 250);
    return () => clearTimeout(t);
  }, [folderOpen, folderTab, folderQuery]);

  // Recompile the full prompt from segments whenever a slot is edited. Strip our
  // template braces so slot text can never inject {TOKEN} placeholders.
  const editSlot = (i, val) => {
    const clean = val.replace(/[{}]/g, "");
    const next = flexSegments.map((s, idx) => (idx === i ? { ...s, v: clean } : s));
    setFlexSegments(next);
    setPrompt(next.map((s) => s.v).join(""));
  };

  const applySaved = (p) => {
    setPrompt(p.prompt_text || "");
    if (p.ratio) setRatio(p.ratio);
    if (p.stage != null && p.stage !== "") setStageNum(String(p.stage));
    setActiveSkin(p.skin || null);
    setStyle(p.style || null);
    if (p.template) setTemplateOn(true);
    setFlexSegments(Array.isArray(p.segments) && p.segments.length ? p.segments : null);
    setFolderOpen(false);
    toast.success(`Loaded "${p.name}"`);
  };
  const savePrompt = async (scope) => {
    const name = (saveName.trim() || fileName.trim());
    if (!name) { toast.error("Name your prompt first"); return; }
    const text = prompt.trim();
    if (!text) { toast.error("Nothing to save"); return; }
    setSaveBusy(true);
    try {
      // Detect editable slots once (at save time), reuse if already parsed.
      let segments = flexSegments;
      if (!segments) {
        try { const r = await promptsApi.parse(text); segments = r.segments || []; setFlexSegments(segments.length ? segments : null); }
        catch { segments = []; }
      }
      const res = await promptsApi.save({ name, prompt_text: text, scope, style, ratio, stage: stageNum, skin: activeSkin, template: templateOn, segments });
      const slotCount = (segments || []).filter((s) => s.t === "slot").length;
      const where = scope === "community" ? `Shared to the community folder as “${res.name}” 🌐` : "Saved to your prompt folder ✓";
      toast.success(slotCount ? `${where} — AI marked ${slotCount} editable area${slotCount > 1 ? "s" : ""}` : where);
      setSaveName("");
    } catch (e) { toast.error(pipelineErr(e, "Save failed")); }
    finally { setSaveBusy(false); }
  };
  const deleteSaved = async (id) => {
    try { await promptsApi.remove(id); setSaved((s) => s.filter((p) => p.id !== id)); toast.success("Prompt deleted"); }
    catch { toast.error("Couldn't delete that prompt"); }
  };
  const starterMatches = STARTER_PROMPTS.filter((s) => !folderQuery.trim() || s.name.toLowerCase().includes(folderQuery.trim().toLowerCase()) || s.prompt_text.toLowerCase().includes(folderQuery.trim().toLowerCase()));

  const reapply = (list) => { if (activeSkin) { const sk = SKIN_PRESETS.find((s) => s.key === activeSkin); if (sk) { setFlexSegments(null); setPrompt(applySkin(sk, rolesOf(list), stageNum)); } } };
  const addRef = async (e) => {
    const f = (e.target.files || [])[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    const role = pendingRole.current || "ref";
    if (refs.filter((r) => r.role === role).length >= REF_CAP[role]) { toast.error("Slot limit reached for this group"); return; }
    try {
      const box = ratio === "1:1" ? { maxW: 1024, maxH: 1024 }
        : ratio === "9:16" ? { maxW: 576, maxH: 1024 }
        : { maxW: 1024, maxH: 576 };
      const compressed = await compressToTarget(f, { ...box, targetKB: 300 });
      const local = previewUrl(compressed);
      const r = await pipelinesApi.uploadSeed(compressed);
      if (r.asset_id) setRefs((p) => { const next = [...p, { id: r.asset_id, src: local, role }]; reapply(next); return next; });
      else { toast.error("Reference upload failed"); if (local) URL.revokeObjectURL(local); }
    } catch (err) { toast.error(pipelineErr(err, "Reference upload failed")); }
  };
  const removeRef = (id) => setRefs((p) => { const next = p.filter((r) => r.id !== id); reapply(next); return next; });

  // Flattened layout guide → upload → prepend to reference ids on Generate.
  const applyLayout = async (file) => {
    try {
      const r = await pipelinesApi.uploadSeed(file);
      if (r.asset_id) { setLayoutGuide({ id: r.asset_id, src: previewUrl(file) }); setComposerOpen(false); toast.success("Layout captured — it'll steer the placement ✓"); }
      else toast.error("Layout upload failed");
    } catch (e) { toast.error(pipelineErr(e, "Layout upload failed")); }
  };

  const toggleTemplate = () => {
    setFlexSegments(null);
    if (templateOn) { setTemplateOn(false); setActiveSkin(null); setPrompt(""); }
    else { setTemplateOn(true); setPrompt(GAME_TEMPLATE.split("{STAGE_NUMBER}").join(stageNum || "1")); setRatio("9:16"); toast.success("Game template loaded — pick a skin or fill each {TOKEN}"); }
  };
  const pickSkin = (p) => { setFlexSegments(null); setActiveSkin(p.key); setPrompt(applySkin(p, rolesOf(refs), stageNum)); setRatio("9:16"); toast.success(`${p.label} skin applied`); };
  const changeStage = (v) => {
    const val = v.replace(/[^0-9]/g, "").slice(0, 3);
    setStageNum(val);
    const shown = val || "1";
    if (activeSkin) { const sk = SKIN_PRESETS.find((s) => s.key === activeSkin); if (sk) setPrompt(applySkin(sk, rolesOf(refs), shown)); }
    else if (templateOn) setPrompt((prev) => prev.replace(/reads "stage [^"]*"/, `reads "stage ${shown}"`));
  };

  const startProgress = () => {
    setProgress(4);
    progTimer.current = setInterval(() => {
      setProgress((p) => (p < 92 ? p + Math.max(1, Math.round((92 - p) / 12)) : p));
    }, 500);
  };
  const stopProgress = () => { if (progTimer.current) { clearInterval(progTimer.current); progTimer.current = null; } };
  useEffect(() => () => stopProgress(), []);

  const runGenerate = async () => {
    const text = prompt.trim();
    if (!text) { toast.error("Describe your vision first"); return; }
    const styleObj = STYLES.find((s) => s.k === style);
    const ratioHint = ratio === "1:1" ? "square 1:1 composition, centered subject, album-cover / social-post framing"
      : ratio === "9:16" ? "vertical 9:16 portrait composition"
      : "widescreen 16:9 landscape composition";
    const layoutHint = layoutGuide ? "Use the FIRST reference image as the exact LAYOUT BLUEPRINT — place each subject at the same position, size and rotation shown there; seamlessly blend the other reference images into those exact spots as one cohesive, photoreal scene." : null;
    const full = [layoutHint, text, styleObj?.desc, ratioHint].filter(Boolean).join(", ");
    const refIds = layoutGuide ? [layoutGuide.id, ...refs.map((r) => r.id)] : refs.map((r) => r.id);

    setStep(2); setResult(null); startProgress(); setRenderBusy(true);
    try {
      const { job_id } = await pipelinesApi.generateImage(full, null, refIds, ratio);
      const j = await pipelinesApi.poll(job_id);
      if (j.status === "completed" && j.asset_id) {
        const name = fileName.trim();
        if (name) { try { await mistApi.renameAsset(j.asset_id, name); } catch { /* non-fatal */ } }
        setResult({ asset: { id: j.asset_id, track: j.track || "image" }, engine: j.engine });
        guardToast(j);
        setProgress(100);
        setTimeout(() => setStep(3), 350);
      } else { toast.error(j.error || "Image generation failed"); setStep(1); }
    } catch (e) { toast.error(pipelineErr(e, "Generation failed")); setStep(1); }
    finally { stopProgress(); setRenderBusy(false); }
  };

  const goBack = () => { setStep(1); setProgress(0); };          // keeps all inputs
  const resetAll = () => {                                        // pristine blank
    stopProgress();
    setStep(1); setPrompt(""); setFileName(""); setStyle(null);
    setRatio("9:16"); setRefs([]); setResult(null); setProgress(0); setSaveName(""); setFlexSegments(null);
    setLayoutGuide(null); setComposerOpen(false);
    toast.success("Cleared — fresh start");
  };

  /* ---------------------------- STEP 1 — CONFIG --------------------------- */
  if (step === 1) {
    if (collageOn) {
      return (
        <div className="h-full w-full flex flex-col min-h-0 bg-slate-950" data-testid="wizard-step1-collage">
          <CollageComposer ratio={ratio} onExit={() => setCollageOn(false)} />
        </div>
      );
    }
    if (cadOn) {
      return (
        <div className="h-full w-full flex flex-col min-h-0 bg-slate-950" data-testid="wizard-step1-cad">
          <VanCabinetDesigner onExit={() => setCadOn(false)} />
        </div>
      );
    }
    return (
      <div className="h-full w-full flex flex-col min-h-0 bg-slate-950 text-slate-100" data-testid="wizard-step1">
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pt-4 pb-4 space-y-5">
          {/* Collage mode toggle — non-text photo collage builder */}
          <button onClick={() => setCollageOn(true)} data-testid="collage-toggle"
            className="w-full flex items-center justify-between gap-3 rounded-xl border border-slate-700 bg-slate-900 px-3.5 py-3 transition-colors">
            <span className="text-left">
              <span className="block font-mono text-[11px] font-black uppercase tracking-widest text-cyan-300">Collage Template</span>
              <span className="block font-mono text-[9px] text-slate-400 mt-0.5">Drop 2–5 photos into framed boxes — no typing</span>
            </span>
            <span className="shrink-0 w-11 h-6 rounded-full flex items-center px-0.5 bg-slate-600 justify-start">
              <span className="w-5 h-5 rounded-full bg-white" />
            </span>
          </button>

          {/* Founder + Business only — parametric CAD (van cabinet / flat-pack) */}
          {canCad && (
            <button onClick={() => setCadOn(true)} data-testid="cad-toggle"
              className="w-full flex items-center justify-between gap-3 rounded-xl border border-amber-500/50 bg-amber-500/5 px-3.5 py-3 transition-colors">
              <span className="text-left">
                <span className="block font-mono text-[11px] font-black uppercase tracking-widest text-amber-300">Van Flatpack · CAD</span>
                <span className="block font-mono text-[9px] text-slate-400 mt-0.5">Founder &amp; Business · parametric spec + PDF · free</span>
              </span>
              <span className="shrink-0 w-11 h-6 rounded-full flex items-center px-0.5 bg-slate-600 justify-start">
                <span className="w-5 h-5 rounded-full bg-white" />
              </span>
            </button>
          )}

          {/* 0 · Game screenshot template toggle */}
          <button onClick={toggleTemplate} data-testid="wizard-template-toggle"
            className={`w-full flex items-center justify-between gap-3 rounded-xl border px-3.5 py-3 transition-colors ${templateOn ? "border-cyan-400 bg-cyan-400/15" : "border-slate-700 bg-slate-900"}`}>
            <span className="text-left">
              <span className="block font-mono text-[11px] font-black uppercase tracking-widest text-cyan-300">Game Screenshot Template</span>
              <span className="block font-mono text-[9px] text-slate-400 mt-0.5">Loads the positioning prompt — fill each {"{TOKEN}"}</span>
            </span>
            <span className={`shrink-0 w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${templateOn ? "bg-cyan-400 justify-end" : "bg-slate-600 justify-start"}`}>
              <span className="w-5 h-5 rounded-full bg-white" />
            </span>
          </button>

          {/* 0b · Prompt Folder — searchable saved-prompt library */}
          <button onClick={() => setFolderOpen(true)} data-testid="wizard-folder-open"
            className="w-full flex items-center justify-between gap-3 rounded-xl border border-slate-700 bg-slate-900 px-3.5 py-3 active:scale-[0.99] transition-transform">
            <span className="flex items-center gap-2">
              <FolderOpen size={15} className="text-cyan-300" />
              <span className="font-mono text-[11px] font-black uppercase tracking-widest text-cyan-300">Prompt Folder</span>
            </span>
            <span className="font-mono text-[9px] text-slate-400">Search &amp; load saved prompts →</span>
          </button>

          {/* 1 · Reference images (template = Hero/Enemy/Boss zones) */}
          <input ref={refInput} type="file" accept="image/*" hidden onChange={addRef} data-testid="wizard-ref-input" />
          {templateOn ? (
            <div className="space-y-3" data-testid="wizard-ref-zones">
              {[
                { role: "hero", label: "Hero Characters", cap: 5, hint: "Up to 5 · each stands on a numbered placement circle" },
                { role: "soldier", label: "Mini Soldier", cap: 1, hint: "Optional · forms a heavy U-shape wall wrapping behind the heroes" },
                { role: "enemy", label: "Enemy Variants", cap: 4, hint: "Up to 4 · zombies / robots / bad guys stacked in lanes" },
                { role: "boss", label: "Boss Enemy", cap: 1, hint: "Optional · always positioned top-center of the road" },
              ].map((z) => {
                const zr = refs.filter((r) => r.role === z.role);
                return (
                  <div key={z.role} data-testid={`wizard-zone-${z.role}`}>
                    <p className="font-mono text-[10px] font-black uppercase tracking-[0.16em] text-cyan-400">{z.label}</p>
                    <p className="font-mono text-[8px] text-slate-500 mb-1.5">{z.hint}</p>
                    <div className="flex flex-wrap gap-2">
                      {Array.from({ length: z.cap }).map((_, i) => {
                        const r = zr[i];
                        if (r) return (
                          <div key={r.id} className="relative w-[52px] h-[52px] rounded-lg overflow-hidden border border-cyan-400/40" data-testid={`wizard-ref-${r.id}`}>
                            <img src={r.src} alt={z.role} className="w-full h-full object-cover" />
                            <span className="absolute bottom-0.5 left-0.5 w-4 h-4 grid place-items-center rounded-full bg-cyan-400 text-slate-950 font-mono text-[8px] font-black">{i + 1}</span>
                            <button onClick={() => removeRef(r.id)} data-testid={`wizard-ref-remove-${r.id}`} className="absolute top-0.5 right-0.5 w-4 h-4 grid place-items-center rounded-full bg-black/70 text-white border border-white/40"><X size={9} /></button>
                          </div>
                        );
                        return (
                          <button key={`${z.role}-${i}`} onClick={() => { pendingRole.current = z.role; refInput.current?.click(); }} data-testid={`wizard-add-${z.role}-${i}`}
                            className="relative w-[52px] h-[52px] flex flex-col items-center justify-center gap-0.5 rounded-lg border-2 border-dashed border-slate-600 text-slate-500 active:scale-95">
                            <Plus size={14} />
                            <span className="absolute bottom-0.5 left-0.5 font-mono text-[8px] text-slate-600">{i + 1}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              <p className="font-mono text-[8px] text-slate-500">Every group is optional — empty groups fall back to the skin's default theme.</p>
            </div>
          ) : (
            <div data-testid="wizard-refs">
              <p className="font-mono text-[10px] font-black uppercase tracking-[0.18em] text-cyan-400 mb-1.5">Reference Images</p>
              <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-1">
                {refs.map((r) => (
                  <div key={r.id} className="relative shrink-0 w-20 h-20 rounded-xl overflow-hidden border border-cyan-400/40" data-testid={`wizard-ref-${r.id}`}>
                    <img src={r.src} alt="ref" className="w-full h-full object-cover" />
                    <button onClick={() => removeRef(r.id)} data-testid={`wizard-ref-remove-${r.id}`} className="absolute top-1 right-1 w-5 h-5 grid place-items-center rounded-full bg-black/70 text-white border border-white/40"><X size={11} /></button>
                  </div>
                ))}
                {refs.length < 5 && (
                  <button onClick={() => { pendingRole.current = "ref"; refInput.current?.click(); }} data-testid="wizard-ref-add"
                    className="shrink-0 w-20 h-20 flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-600 text-slate-400 active:scale-95">
                    <Plus size={18} /><span className="font-mono text-[7px] uppercase tracking-wide">Add</span>
                  </button>
                )}
              </div>
              {refs.length === 0 && <p className="mt-1 font-mono text-[9px] text-slate-500">Optional: Add up to 5 reference images</p>}

              {/* Drag-&-place Layout Composer toggle (needs a background + a stamp) */}
              {refs.length >= 2 && (
                <button onClick={() => setComposerOpen(true)} data-testid="wizard-composer-open"
                  className="mt-2.5 w-full flex items-center justify-between gap-3 rounded-xl border border-fuchsia-400/40 bg-fuchsia-500/[0.08] px-3.5 py-3 active:scale-[0.99]">
                  <span className="flex items-center gap-2">
                    <Layers size={15} className="text-fuchsia-300" />
                    <span className="font-mono text-[11px] font-black uppercase tracking-widest text-fuchsia-300">Arrange on Canvas</span>
                  </span>
                  <span className="font-mono text-[9px] text-slate-400">Place refs → auto-position →</span>
                </button>
              )}
              {layoutGuide && (
                <div className="mt-2 flex items-center gap-2 rounded-xl border border-fuchsia-400/40 bg-fuchsia-500/[0.08] p-2" data-testid="wizard-layout-chip">
                  <img src={layoutGuide.src} alt="layout" className="w-10 h-10 rounded object-cover border border-fuchsia-400/40" />
                  <span className="flex-1 font-mono text-[9px] text-fuchsia-200">Layout blueprint attached — it guides element placement.</span>
                  <button onClick={() => setComposerOpen(true)} data-testid="wizard-layout-edit" className="font-mono text-[9px] font-black uppercase text-cyan-300 px-2 py-1">Edit</button>
                  <button onClick={() => setLayoutGuide(null)} data-testid="wizard-layout-clear" className="w-6 h-6 grid place-items-center rounded-full bg-black/50 text-white border border-white/30"><X size={11} /></button>
                </div>
              )}
            </div>
          )}

          {/* 2 · Name File */}
          <label className="block" data-testid="wizard-name-field">
            <span className="font-mono text-[10px] font-black uppercase tracking-[0.18em] text-cyan-400">Name File</span>
            <input value={fileName} onChange={(e) => setFileName(e.target.value.slice(0, 80))} data-testid="wizard-name-input"
              placeholder="Enter image name..."
              className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-xl px-3.5 py-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/30" />
          </label>

          {/* 3 · Skin presets (template mode) OR style grid + aspect ratio */}
          {templateOn ? (
            <div data-testid="wizard-skins">
              <label className="block mb-3" data-testid="wizard-stage-field">
                <span className="font-mono text-[10px] font-black uppercase tracking-[0.18em] text-cyan-400">Stage Number</span>
                <span className="block font-mono text-[9px] text-slate-500 mb-1.5">Sets the "stage N" HUD text in the top-left corner.</span>
                <input value={stageNum} onChange={(e) => changeStage(e.target.value)} inputMode="numeric" data-testid="wizard-stage-input"
                  placeholder="1"
                  className="w-24 bg-slate-900 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/30" />
              </label>
              <p className="font-mono text-[10px] font-black uppercase tracking-[0.18em] text-cyan-400 mb-1">Skin Preset</p>
              <p className="font-mono text-[9px] text-slate-500 mb-1.5">Pick a theme to auto-fill every slot — then tweak the prompt below.</p>
              <div className="grid grid-cols-1 gap-2">
                {SKIN_PRESETS.map((p) => (
                  <button key={p.key} onClick={() => pickSkin(p)} data-testid={`wizard-skin-${p.key}`}
                    className={`text-left rounded-xl border px-3.5 py-3 font-mono text-[11px] font-bold transition-colors ${activeSkin === p.key ? "border-cyan-400 bg-cyan-400/15 text-cyan-200" : "border-slate-700 bg-slate-900 text-slate-300"}`}>
                    {p.emoji} {p.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
          <>
          <div data-testid="wizard-style">
            <p className="font-mono text-[10px] font-black uppercase tracking-[0.18em] text-cyan-400 mb-1">Style <span className="text-slate-500 font-normal normal-case tracking-normal">· optional</span></p>
            <p className="font-mono text-[9px] text-slate-500 mb-1.5">Leave unselected for a realistic or prompt-driven look (e.g. "make an anime character"). Tap a selected style again to remove it.</p>
            <div className="grid grid-cols-1 gap-2">
              {STYLES.map((s) => (
                <button key={s.k} onClick={() => setStyle(style === s.k ? null : s.k)} data-testid={`wizard-style-${s.k}`}
                  className={`text-left rounded-xl border px-3.5 py-3 font-mono text-[11px] font-bold transition-colors ${style === s.k ? "border-cyan-400 bg-cyan-400/15 text-cyan-200" : "border-slate-700 bg-slate-900 text-slate-300"}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div data-testid="wizard-ratio">
            <p className="font-mono text-[10px] font-black uppercase tracking-[0.18em] text-cyan-400 mb-1.5">Aspect Ratio</p>
            <div className="grid grid-cols-3 gap-2">
              {[["1:1", "Album"], ["9:16", "Reel"], ["16:9", "Wide"]].map(([r, tag]) => (
                <button key={r} onClick={() => setRatio(r)} data-testid={`wizard-ratio-${r.replace(":", "-")}`}
                  className={`flex flex-col items-center gap-0.5 rounded-xl border py-3 font-mono font-black tracking-widest transition-colors ${ratio === r ? "border-cyan-400 bg-cyan-400 text-slate-950" : "border-slate-700 bg-slate-900 text-slate-300"}`}>
                  <span className="text-xs">{r}</span>
                  <span className="text-[7px] uppercase opacity-70">{tag}</span>
                </button>
              ))}
            </div>
          </div>
          </>
          )}

          {/* 4 · Prompt */}
          {flexSegments && <FlexSlotEditor segments={flexSegments} onSlot={editSlot} />}
          <label className="block" data-testid="wizard-prompt-field">
            <span className="font-mono text-[10px] font-black uppercase tracking-[0.18em] text-cyan-400">Text Prompt</span>
            <textarea value={prompt} onChange={(e) => { setFlexSegments(null); setPrompt(e.target.value.slice(0, MAX_CHARS)); }} rows={5} data-testid="wizard-prompt"
              placeholder="Describe your vision here..."
              className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-xl p-3.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none resize-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/30" />
            <span className="mt-1 block text-right font-mono text-[9px] text-slate-500">{prompt.length} / {MAX_CHARS}</span>
          </label>
        </div>

        {/* 5 · Generate */}
        <div className="shrink-0 border-t border-slate-800 bg-slate-950 px-4 py-3">
          <button onClick={runGenerate} disabled={!prompt.trim()} data-testid="wizard-generate"
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-950 font-black font-mono text-sm uppercase tracking-widest py-4 shadow-[0_0_24px_rgba(56,189,248,0.4)] active:scale-[0.99] disabled:opacity-40 disabled:shadow-none">
            <Sparkles size={17} /> Generate
          </button>
        </div>

        {composerOpen && <LayoutComposer refs={refs} onCancel={() => setComposerOpen(false)} onDone={applyLayout} />}

        {folderOpen && (
          <div className="fixed inset-0 z-[60] bg-slate-950/97 backdrop-blur-sm flex flex-col" data-testid="wizard-folder-modal">
            <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-slate-800">
              <span className="flex items-center gap-2 font-mono text-[12px] font-black uppercase tracking-widest text-cyan-300"><FolderOpen size={16} /> Prompt Folder</span>
              <button onClick={() => setFolderOpen(false)} data-testid="wizard-folder-close" className="w-8 h-8 grid place-items-center rounded-full bg-slate-800 text-slate-200 active:scale-95"><X size={16} /></button>
            </div>
            <div className="shrink-0 px-4 pt-3">
              <div className="flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2.5">
                <Search size={14} className="text-slate-400" />
                <input value={folderQuery} onChange={(e) => setFolderQuery(e.target.value)} data-testid="wizard-folder-search"
                  placeholder="Search prompts by name…" className="flex-1 bg-transparent text-sm text-slate-100 placeholder:text-slate-500 outline-none" />
                {folderQuery && <button onClick={() => setFolderQuery("")} data-testid="wizard-folder-search-clear"><X size={13} className="text-slate-500" /></button>}
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                {[{ k: "personal", label: "Personal", Icon: User }, { k: "community", label: "Community", Icon: Globe }].map((t) => (
                  <button key={t.k} onClick={() => setFolderTab(t.k)} data-testid={`wizard-folder-tab-${t.k}`}
                    className={`flex items-center justify-center gap-2 rounded-xl border py-2.5 font-mono text-[10px] font-black uppercase tracking-widest transition-colors ${folderTab === t.k ? "border-cyan-400 bg-cyan-400/15 text-cyan-200" : "border-slate-700 bg-slate-900 text-slate-400"}`}>
                    <t.Icon size={13} /> {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-3 space-y-2" data-testid="wizard-folder-list">
              {folderTab === "personal" && starterMatches.length > 0 && (
                <>
                  <p className="font-mono text-[9px] font-black uppercase tracking-[0.16em] text-slate-500">Starter Templates</p>
                  {starterMatches.map((p) => <PromptRow key={p.id} p={p} onLoad={() => applySaved(p)} />)}
                </>
              )}
              {folderBusy ? (
                <div className="grid place-items-center py-12"><Loader2 size={22} className="text-cyan-400 animate-spin" /></div>
              ) : (
                <>
                  {saved.length > 0 && <p className="font-mono text-[9px] font-black uppercase tracking-[0.16em] text-slate-500 pt-2">{folderTab === "community" ? "Community Prompts" : "My Saved Prompts"}</p>}
                  {saved.map((p) => <PromptRow key={p.id} p={p} onLoad={() => applySaved(p)} onDelete={p.mine ? () => deleteSaved(p.id) : null} />)}
                  {saved.length === 0 && !(folderTab === "personal" && starterMatches.length > 0) && (
                    <p className="text-center font-mono text-[10px] text-slate-500 py-12">
                      {folderTab === "community" ? "No community prompts yet — be the first to share one." : "No saved prompts yet. Generate an image you like, then save its prompt here."}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  /* --------------------------- STEP 2 — LOADING --------------------------- */
  if (step === 2) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center gap-6 bg-slate-950 px-8" data-testid="wizard-step2">
        <div className="relative w-40 h-40 grid place-items-center rounded-3xl border-2 border-cyan-400/40 bg-slate-900 shadow-[0_0_40px_rgba(56,189,248,0.35)]">
          <ImageIcon size={54} className="text-cyan-400/80" />
          <span className="absolute inset-0 rounded-3xl border-2 border-cyan-400/30 animate-ping" />
        </div>
        <div className="w-full max-w-xs">
          <div className="h-2.5 w-full rounded-full bg-slate-800 overflow-hidden" data-testid="wizard-progress">
            <div className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
          </div>
          <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-300">Rendering your vision… {progress}%</p>
        </div>
      </div>
    );
  }

  /* --------------------------- STEP 3 — RESULT ---------------------------- */
  return (
    <div className="h-full w-full flex flex-col min-h-0 bg-slate-950" data-testid="wizard-step3">
      <div className="h-1/2 min-h-0 shrink-0 bg-black/60 border-b border-slate-800 p-2">
        {result?.asset?.id && <ResultImage assetId={result.asset.id} />}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar p-4 space-y-4">
        <div>
          <p className="font-mono text-[10px] font-black uppercase tracking-[0.18em] text-cyan-400 mb-1.5">Save</p>
          <p className="font-mono text-[9px] text-slate-500 mb-2">Not saved yet — choose where to keep it:</p>
          {result?.asset?.id && <ExportHandlers assetId={result.asset.id} track="image" filename={fileName.trim() || "mist_image"} />}
        </div>

        {/* Save this prompt to the folder — worked well? keep it for reuse. */}
        {prompt.trim() && (
          <div data-testid="wizard-save-prompt">
            <p className="font-mono text-[10px] font-black uppercase tracking-[0.18em] text-cyan-400 mb-1">Save this prompt</p>
            <p className="font-mono text-[9px] text-slate-500 mb-2">Happy with the result? Keep this prompt in your folder to reuse it.</p>
            <input value={saveName} onChange={(e) => setSaveName(e.target.value.slice(0, 80))} data-testid="wizard-save-prompt-name"
              placeholder={fileName.trim() || "Prompt name…"}
              className="w-full mb-2 bg-slate-900 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/30" />
            <div className="grid grid-cols-2 gap-2.5">
              <button onClick={() => savePrompt("personal")} disabled={saveBusy} data-testid="wizard-save-personal"
                className="flex items-center justify-center gap-2 rounded-xl border border-cyan-400/50 bg-cyan-400/10 text-cyan-200 font-mono text-[11px] font-black uppercase tracking-widest py-3 active:scale-[0.99] disabled:opacity-40">
                {saveBusy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Personal
              </button>
              <button onClick={() => savePrompt("community")} disabled={saveBusy} data-testid="wizard-save-community"
                className="flex items-center justify-center gap-2 rounded-xl border border-slate-600 bg-slate-900 text-slate-200 font-mono text-[11px] font-black uppercase tracking-widest py-3 active:scale-[0.99] disabled:opacity-40">
                {saveBusy ? <Loader2 size={13} className="animate-spin" /> : <Globe size={13} />} Community
              </button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2.5">
          <button onClick={goBack} data-testid="wizard-goback"
            className="flex items-center justify-center gap-2 rounded-xl border border-slate-600 bg-slate-900 text-slate-200 font-mono text-[11px] font-black uppercase tracking-widest py-3.5 active:scale-[0.99]">
            <ArrowLeft size={14} /> Go Back
          </button>
          <button onClick={resetAll} data-testid="wizard-reset"
            className="flex items-center justify-center gap-2 rounded-xl border border-rose-500/50 bg-rose-500/10 text-rose-300 font-mono text-[11px] font-black uppercase tracking-widest py-3.5 active:scale-[0.99]">
            <RotateCcw size={14} /> Reset
          </button>
        </div>
      </div>
    </div>
  );
}
